<script setup lang="ts">
import { computed } from 'vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import BaseBadge from '@/components/ui/BaseBadge.vue';
import AiBadgePill from './AiBadgePill.vue';
import type { IssueListItemDto } from '@/stores/issues';
import { SEVERITY_LABEL, SEVERITY_BADGE, STATUS_BADGE, STATUS_LABEL, CATEGORY_LABEL, SOURCE_LABEL } from './issue-enums';

dayjs.extend(relativeTime);

interface Props {
  issue: IssueListItemDto;
}
const props = defineProps<Props>();

const severityVariant = computed(() => SEVERITY_BADGE[props.issue.severity] ?? 'info');
const statusVariant = computed(() => STATUS_BADGE[props.issue.status] ?? 'info');
const aiChanged = computed(
  () =>
    props.issue.aiClassified &&
    props.issue.aiSeverity !== null &&
    props.issue.aiSeverity !== undefined &&
    props.issue.aiSeverity !== props.issue.severity,
);
</script>

<template>
  <tr
    class="hover:bg-canvas-soft transition-colors cursor-pointer border-b border-border last:border-0"
    data-testid="issue-row"
  >
    <!-- Severity badge -->
    <td class="py-3 pl-4 pr-2 w-24">
      <BaseBadge :variant="severityVariant">
        {{ SEVERITY_LABEL[issue.severity] ?? String(issue.severity) }}
      </BaseBadge>
    </td>

    <!-- Title + meta -->
    <td class="py-3 px-2 min-w-0">
      <div class="flex items-start gap-2 min-w-0">
        <div class="flex-1 min-w-0">
          <p class="text-body text-fg font-medium truncate" data-testid="issue-title">{{ issue.title }}</p>
          <p v-if="issue.summary" class="text-caption text-muted truncate mt-0.5">{{ issue.summary }}</p>
          <div class="flex flex-wrap gap-1 mt-1">
            <span v-if="issue.location" class="text-caption text-muted">{{ issue.location }}</span>
            <span class="text-caption text-muted">· {{ CATEGORY_LABEL[issue.category] ?? String(issue.category) }}</span>
            <span v-if="issue.isRepeat" class="text-caption text-orange-600 font-medium">
              · Repeat ({{ issue.repeatCount }}×)
            </span>
          </div>
        </div>
        <AiBadgePill v-if="aiChanged" label="AI" />
      </div>
    </td>

    <!-- Source -->
    <td class="py-3 px-2 hidden md:table-cell">
      <span class="text-caption text-muted">{{ SOURCE_LABEL[issue.source] ?? String(issue.source) }}</span>
    </td>

    <!-- Assignee -->
    <td class="py-3 px-2 hidden lg:table-cell">
      <span v-if="issue.assignedToName" class="text-caption text-fg">{{ issue.assignedToName }}</span>
      <span v-else class="text-caption text-muted-2">—</span>
    </td>

    <!-- Status -->
    <td class="py-3 px-2">
      <BaseBadge :variant="statusVariant" :no-dot="true">
        {{ STATUS_LABEL[issue.status] ?? String(issue.status) }}
      </BaseBadge>
    </td>

    <!-- AI confidence -->
    <td class="py-3 px-2 hidden xl:table-cell w-28">
      <template v-if="issue.aiConfidence !== null && issue.aiConfidence !== undefined">
        <div class="flex items-center gap-2">
          <div class="flex-1 bg-canvas-soft rounded-full h-1.5">
            <div
              class="bg-teal-500 h-1.5 rounded-full"
              :style="{ width: `${issue.aiConfidence}%` }"
            />
          </div>
          <span class="text-caption font-mono tabular-nums text-teal-700 flex-shrink-0">
            {{ issue.aiConfidence }}%
          </span>
        </div>
      </template>
      <span v-else class="text-caption text-muted-2">—</span>
    </td>

    <!-- Updated time -->
    <td class="py-3 pl-2 pr-4 text-right hidden sm:table-cell">
      <span class="text-caption text-muted font-mono tabular-nums">
        {{ dayjs(issue.updatedAt).fromNow() }}
      </span>
    </td>
  </tr>
</template>
